import { isEqual } from "lodash"
import { CUSTOM_ATTRIBUTE_DND_TYPE_CHILD, CUSTOM_ATTRIBUTE_DND_TYPE_PARENT, ProductCustomAttributesSelectedOptionOrChoice } from "../../common/types"
import { ItemAttributes } from "../../state/pricing/Item"
import ProductCustomAttributeDefintion, {
    ItemCustomAttributeValue,
    ProductCustomDefinitionAttributes,
    ProductCustomDefinitionAttributesLabel,
    ProductCustomDefinitionDto,
    ProductCustomDefinitionImage,
    ProductCustomDefinitionImageMetadata
} from "../../state/product-custom-attributes/CustomAttributeDefinition"
import {
    CUSTOM_ATTRIBUTE_DND_DROPPABLE,
    CUSTOM_ATTRIBUTE_DND_TYPE_CONSTANTS,
    CUSTOM_ATTRIBUTE_EVENT,
    CUSTOM_ATTRIBUTE_TYPE_SELECT,
    CUSTOM_ATTRIBUTE_TYPE_TEXT,
    DEFAULT_CUSTOM_ATTRIBUTE,
    DEFAULT_CUSTOM_ATTRIBUTE_MULTI_IMAGE_ATTRIBUTE,
    ITEM_TYPE,
    itemCustomAttributesMessages
} from "./constant"

export const mapCustomAttributesFromDto = (customAttributes: ProductCustomAttributeDefintion[]): ProductCustomDefinitionAttributes[] => {
    return (
        (customAttributes || [])
            // We don't display the attributes when they marked toDelete
            .map(customDef => ({
                id: customDef.getId(),
                attributeType: customDef.getFieldType(),
                customFieldDefinitionLabel: customDef.getLabel(),
                required: customDef.isRequired(),
                allowMoreThanOneChoice: customDef.allowMoreThanOneChoice(),
                toDelete: customDef.getToDelete()
            }))
            .filter(e => {
                // Always include attribute that has valid id, because we need either update or delete it
                if (e.id !== "") {
                    return true
                }
                // Exclude those local attributes that need to be deleted
                if (e.id === "" && e.toDelete) {
                    return false
                }
                return true
            })
    )
}

export const mapCustomAttributesDtoFromObject = (customAttributes: ProductCustomDefinitionAttributes[]): ProductCustomDefinitionDto[] => {
    return [...customAttributes].map((e: ProductCustomDefinitionAttributes) => ({
        attributes: {
            ...e
        },
        id: e.id || "",
        type: e.attributeType
    }))
}

export const handleCustomAttributeOperation = (
    customAttributes: ProductCustomDefinitionAttributes[],
    index: number | undefined,
    value: string | undefined,
    operation: string,
    cb: (customAttributes: ProductCustomDefinitionDto[]) => void
): void => {
    switch (operation) {
    case CUSTOM_ATTRIBUTE_EVENT.ADD_ATTRIBUTE:
        {
            const newfield: ProductCustomDefinitionAttributes = {
                ...DEFAULT_CUSTOM_ATTRIBUTE
            }
            const updated = [...customAttributes, newfield].map((e: ProductCustomDefinitionAttributes) => ({
                attributes: {
                    ...e,
                    toDelete: e.toDelete
                },
                id: e.id,
                type: e.attributeType
            }))
            cb(updated)
        }
        break
    case CUSTOM_ATTRIBUTE_EVENT.DELETE_ATTRIBUTE:
        {
            const updated = [...customAttributes].map((e: ProductCustomDefinitionAttributes, i: number) => {
                if (i === index) {
                    return {
                        ...e,
                        toDelete: true,
                        id: e.id,
                        type: e.attributeType
                    }
                }

                return {
                    ...e,
                    id: e.id,
                    type: e.attributeType
                }
            })
            const mappedCustomAttributes = mapCustomAttributesDtoFromObject(updated)
            cb(mappedCustomAttributes)
        }
        break
    case CUSTOM_ATTRIBUTE_EVENT.CHECK_REQUIRED:
        {
            const updatedCustomAttributes = customAttributes.map((e: ProductCustomDefinitionAttributes, i: number) => {
                if (i === index) {
                    return {
                        ...e,
                        required: !e.required
                    }
                } else {
                    return { ...e }
                }
            })
            const mappedCustomAttributes = mapCustomAttributesDtoFromObject(updatedCustomAttributes)
            cb(mappedCustomAttributes)
        }
        break
    case CUSTOM_ATTRIBUTE_EVENT.CHECK_ALLOW_MORE_THAN_ONE_CHOICE:
        {
            const updatedCustomAttributes = customAttributes.map((e: ProductCustomDefinitionAttributes, i: number) => {
                if (i === index) {
                    return {
                        ...e,
                        allowMoreThanOneChoice: !e.allowMoreThanOneChoice
                    }
                } else {
                    return { ...e }
                }
            })
            const mappedCustomAttributes = mapCustomAttributesDtoFromObject(updatedCustomAttributes)
            cb(mappedCustomAttributes)
        }
        break
    case CUSTOM_ATTRIBUTE_EVENT.UPDATE_ATTRIBUTE_LABEL:
        {
            const updatedCustomAttributes = [...customAttributes].map((e: ProductCustomDefinitionAttributes, i: number) => {
                if (i === index) {
                    return {
                        ...e,
                        customFieldDefinitionLabel: {
                            label: value as string,
                            metadata: [...(e.customFieldDefinitionLabel?.metadata || [])]
                        }
                    }
                } else {
                    return { ...e }
                }
            })
            const mappedCustomAttributes = mapCustomAttributesDtoFromObject(updatedCustomAttributes)
            cb(mappedCustomAttributes)
        }
        break
    case CUSTOM_ATTRIBUTE_EVENT.SELECT_ATTRIBUTE_TYPE:
        {
            const updated = [...customAttributes].map((e: ProductCustomDefinitionAttributes, i: number) => {
                if (index === i) {
                    if (value === CUSTOM_ATTRIBUTE_TYPE_SELECT.value) {
                        return {
                            ...e,
                            attributeType: value as string,
                            customFieldDefinitionLabel: {
                                ...DEFAULT_CUSTOM_ATTRIBUTE.customFieldDefinitionLabel,
                                label: e.customFieldDefinitionLabel?.label || "",
                                metadata:
                                        // Only add a default sub select when metadata is empty array
                                        (e.customFieldDefinitionLabel?.metadata || []).length > 0
                                            ? [...(e.customFieldDefinitionLabel?.metadata || [])]
                                            : [
                                                ...(e.customFieldDefinitionLabel?.metadata || []),
                                                { ...DEFAULT_CUSTOM_ATTRIBUTE_MULTI_IMAGE_ATTRIBUTE }
                                            ]
                            }
                        }
                    }

                    return {
                        ...e,
                        allowMoreThanOneChoice: false,
                        customFieldDefinitionLabel: {
                            ...e.customFieldDefinitionLabel,
                            label: e.customFieldDefinitionLabel?.label || "",
                            metadata: []
                        },
                        attributeType: value as string,
                        type: e.attributeType
                    }
                }
                return {
                    ...e,
                    id: e.id,
                    type: e.attributeType
                }
            })
            const mappedCustomAttributes = mapCustomAttributesDtoFromObject(updated)
            cb(mappedCustomAttributes)
        }
        break
    default:
        return
    }
}

export const handleCustomAttributeMutliImageOperation = (
    customAttributes: ProductCustomDefinitionAttributes[],
    attributeIndex: number,
    index: number | undefined,
    value: string | ProductCustomDefinitionImage | undefined,
    operation: string,
    cb: (customAttributes: ProductCustomDefinitionDto[]) => void
): void => {
    switch (operation) {
    case CUSTOM_ATTRIBUTE_EVENT.ADD_ATTRIBUTE:
        {
            const updated = [...customAttributes].map((e: ProductCustomDefinitionAttributes, i: number) => {
                if (i === attributeIndex) {
                    return {
                        attributes: {
                            ...e,
                            attributeType: CUSTOM_ATTRIBUTE_TYPE_SELECT.value,
                            customFieldDefinitionLabel: {
                                ...DEFAULT_CUSTOM_ATTRIBUTE.customFieldDefinitionLabel,
                                label: e.customFieldDefinitionLabel?.label || "",
                                metadata: [
                                    ...(e.customFieldDefinitionLabel?.metadata || []),
                                    { ...DEFAULT_CUSTOM_ATTRIBUTE_MULTI_IMAGE_ATTRIBUTE }
                                ]
                            }
                        },
                        id: e.id,
                        type: e.attributeType
                    }
                }
                return {
                    attributes: {
                        ...e
                    },
                    id: e.id,
                    type: e.attributeType
                }
            })
            cb(updated)
        }
        break
    case CUSTOM_ATTRIBUTE_EVENT.DELETE_ATTRIBUTE:
        {
            const updated = [...customAttributes].map((e: ProductCustomDefinitionAttributes, i: number) => {
                if (i === attributeIndex) {
                    return {
                        attributes: {
                            ...e,
                            attributeType: "select",
                            customFieldDefinitionLabel: {
                                ...DEFAULT_CUSTOM_ATTRIBUTE.customFieldDefinitionLabel,
                                label: e.customFieldDefinitionLabel?.label || "",
                                metadata: (e.customFieldDefinitionLabel?.metadata || []).filter((e, i) => i !== index)
                            }
                        },
                        id: e.id,
                        type: e.attributeType
                    }
                }
                return {
                    attributes: {
                        ...e
                    },
                    id: e.id,
                    type: e.attributeType
                }
            })
            cb(updated)
        }
        break
    case CUSTOM_ATTRIBUTE_EVENT.UPDATE_ATTRIBUTE_LABEL:
        {
            const updatedCustomAttributes = [...customAttributes].map((e: ProductCustomDefinitionAttributes, outterIndex: number) => {
                if (outterIndex === attributeIndex) {
                    return {
                        ...e,
                        customFieldDefinitionLabel: {
                            ...e.customFieldDefinitionLabel,
                            metadata: (e.customFieldDefinitionLabel?.metadata || []).map(
                                (e: ProductCustomDefinitionImageMetadata, innerIndex: number) => {
                                    if (innerIndex === index) {
                                        return {
                                            ...e,
                                            label: value as string
                                        }
                                    }
                                    return { ...e }
                                }
                            )
                        }
                    }
                } else {
                    return { ...e }
                }
            })
            const mappedCustomAttributes = mapCustomAttributesDtoFromObject(updatedCustomAttributes)
            cb(mappedCustomAttributes)
        }
        break
    case CUSTOM_ATTRIBUTE_EVENT.UPDATE_MULTI_IMAGE_SELECT_ID:
        {
            const updatedCustomAttributes = [...customAttributes].map(
                (definitionAttributes: ProductCustomDefinitionAttributes, outterIndex: number) => {
                    if (outterIndex === attributeIndex) {
                        return {
                            ...definitionAttributes,
                            customFieldDefinitionLabel: {
                                ...definitionAttributes.customFieldDefinitionLabel,
                                metadata: [...(definitionAttributes.customFieldDefinitionLabel?.metadata || [])].map(
                                    (metadata: ProductCustomDefinitionImageMetadata, innerIndex: number) => {
                                        if (innerIndex === index) {
                                            const updated = {
                                                label: metadata.label,
                                                resources: value ? [(value as ProductCustomDefinitionImage)?.id] : [],
                                                images: value ? [value as ProductCustomDefinitionImage] : [],
                                                id: metadata.id
                                            }
                                            return updated
                                        }
                                        return {
                                            ...metadata
                                        }
                                    }
                                )
                            }
                        }
                    } else {
                        return {
                            ...definitionAttributes
                        }
                    }
                }
            )
            const mappedCustomAttributes = mapCustomAttributesDtoFromObject(updatedCustomAttributes)
            cb(mappedCustomAttributes)
        }
        break
    default:
        return
    }
}

export const reorder = (
    list: Array<ProductCustomDefinitionAttributes> | Array<ProductCustomDefinitionImageMetadata>,
    startIndex: number,
    endIndex: number,
    type: CUSTOM_ATTRIBUTE_DND_TYPE_PARENT | CUSTOM_ATTRIBUTE_DND_TYPE_CHILD
) => {
    let result
    if (type === CUSTOM_ATTRIBUTE_DND_TYPE_CONSTANTS.PARENT) {
        result = Array.from(list as Array<ProductCustomDefinitionAttributes>)
    } else if (type === CUSTOM_ATTRIBUTE_DND_TYPE_CONSTANTS.CHILD) {
        result = Array.from(list as Array<ProductCustomDefinitionImageMetadata>)
    }
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)
    return result
}

export const handleDrop = (
    result,
    customAttributes: ProductCustomDefinitionAttributes[],
    cb: (customAttributes: ProductCustomDefinitionDto[]) => void
) => {
    if (!result.destination) {
        return
    }
    const sourceIndex = result.source.index
    const destIndex = result.destination.index
    if (result.type === CUSTOM_ATTRIBUTE_DND_DROPPABLE.ITEM) {
        const reorderedAttributes = reorder(customAttributes, sourceIndex, destIndex, "parent")
        const updated = [...reorderedAttributes].map((e: ProductCustomDefinitionAttributes) => ({
            attributes: {
                ...e
            },
            id: e.id,
            type: e.attributeType
        }))
        cb(updated)
    } else if (result.type === CUSTOM_ATTRIBUTE_DND_DROPPABLE.SUB_ITEM) {
        const parentItemIndex = result.destination.droppableId
        const subItem = [...customAttributes][parentItemIndex].customFieldDefinitionLabel.metadata
        const sourceParentId = parseInt(result.source.droppableId)
        const destParentId = parseInt(result.destination.droppableId)
        let newItems = [...customAttributes]

        /** In this case subItems are reOrdered inside same Parent */
        if (sourceParentId === destParentId) {
            const reorderedSubItems = reorder(subItem, sourceIndex, destIndex, "child")
            newItems = newItems.map((item, index) => {
                if (index === sourceParentId) {
                    return {
                        ...item,
                        customFieldDefinitionLabel: {
                            ...item.customFieldDefinitionLabel,
                            metadata: reorderedSubItems
                        }
                    }
                }
                return item
            })
            const updated = [...newItems].map((e: ProductCustomDefinitionAttributes) => ({
                attributes: {
                    ...e
                },
                id: e.id,
                type: e.attributeType
            }))
            cb(updated)
        }
    }
}

export const getNumberOfInvalidLabelsAndImageUpload = (customAttributes: ProductCustomDefinitionAttributes[] = []): number => {
    let count = 0
    customAttributes
        // Exclude the to delete ones
        .filter(attribute => !attribute.toDelete)
        .forEach(attribute => {
            if (attribute.attributeType === CUSTOM_ATTRIBUTE_TYPE_TEXT.value) {
                // Check first layer lable first
                if (!attribute.customFieldDefinitionLabel) {
                    count++
                }
                if (attribute.customFieldDefinitionLabel?.label === "") {
                    count++
                }
                if (attribute.customFieldDefinitionLabel?.label?.trim() === "") {
                    count++
                }
            }
            if (attribute.attributeType === CUSTOM_ATTRIBUTE_TYPE_SELECT.value) {
                // Check second layer: metadata labels
                if (Array.isArray(attribute.customFieldDefinitionLabel?.metadata)) {
                    if (attribute.customFieldDefinitionLabel?.metadata?.length === 0) {
                        count++
                    }
                    (attribute.customFieldDefinitionLabel?.metadata || []).forEach(e => {
                        if (e.label === "" || e.label?.trim() === "" || !e.label) {
                            count++
                        }
                    })
                }
            }
        })
    return count
}

export const isFirstLayerLabelInvaid = (label: ProductCustomDefinitionAttributesLabel = { label: "", metadata: [] }): boolean => {
    let isInvalid = false
    if (!label) {
        isInvalid = true
    }
    if (label?.label === "") {
        isInvalid = true
    }
    if (label?.label?.trim() === "") {
        isInvalid = true
    }
    return isInvalid
}

export const isMetadataInvalid = (metadata: Array<ProductCustomDefinitionImageMetadata> = []) => {
    let isInvalid = false
    if ((metadata || []).length === 0) {
        isInvalid = true
    }
    if ((metadata || []).some(e => e.label === "")) {
        isInvalid = true
    }
    if ((metadata || []).some(e => e.label?.trim() === "")) {
        isInvalid = true
    }
    return isInvalid
}

export const checkEmptyLabelsAndImageUpload = (
    customAttributes: ProductCustomDefinitionAttributes[],
    cb: (containsEmptyField: boolean) => void
): void => {
    const containsInvalidField = customAttributes
        .filter(attribute => !attribute.toDelete)
        .some(e => {
            if (e.attributeType === CUSTOM_ATTRIBUTE_TYPE_TEXT.value) {
                return isFirstLayerLabelInvaid(e.customFieldDefinitionLabel)
            }
            if (e.attributeType === CUSTOM_ATTRIBUTE_TYPE_SELECT.value) {
                return isFirstLayerLabelInvaid(e.customFieldDefinitionLabel) || isMetadataInvalid(e.customFieldDefinitionLabel.metadata)
            }
        })
    cb(containsInvalidField)
}

export const getShouldDisplayValues = (
    item: ItemAttributes,
    choicesSelected: ProductCustomAttributesSelectedOptionOrChoice,
    optionsSelected: ProductCustomAttributesSelectedOptionOrChoice
): boolean => {
    let shouldDisplay = false
    if (choicesSelected[item.id]) {
        shouldDisplay = true
    }
    if (optionsSelected[item.id]) {
        shouldDisplay = true
    }
    if (item.isIncluded) {
        shouldDisplay = true
    }
    if (item.type === ITEM_TYPE.ACTIVE) {
        shouldDisplay = true
    }
    if (item.type == ITEM_TYPE.PROVIDED_BY_CUSTOMER) {
        shouldDisplay = true
    }
    return shouldDisplay
}

export const generateFilteredMetadata = (
    isEditable: boolean,
    metadata: Array<ProductCustomDefinitionImageMetadata>,
    selectValue: Array<string>
): Array<ProductCustomDefinitionImageMetadata> => {
    if (isEditable) {
        return metadata
    } else {
        const selectMap = {}
        selectValue.forEach(element => {
            selectMap[element] = true
        })
        return metadata.filter(e => {
            const key = e.id || ""
            return !!selectMap[key]
        })
    }
}

export const getProductCustomDefinitionAttributesMetadata = (
    customAttributes: Array<ProductCustomDefinitionAttributes>,
    attributeIndex: number
): Array<ProductCustomDefinitionImageMetadata> => customAttributes[attributeIndex].customFieldDefinitionLabel?.metadata

export const getProductCustomDefinitionAttributesRequired = (
    customAttributes: Array<ProductCustomDefinitionAttributes>,
    attributeIndex: number
): boolean => customAttributes[attributeIndex].required

export const getProductCustomDefinitionAttributesAllowMoreThanOneChoice = (
    customAttributes: Array<ProductCustomDefinitionAttributes>,
    attributeIndex: number
): boolean => customAttributes[attributeIndex].allowMoreThanOneChoice

export const getProductCustomDefinitionAttributesFirstLayerLabel = (
    customAttributes: Array<ProductCustomDefinitionAttributes>,
    attributeIndex: number
): string => customAttributes[attributeIndex].customFieldDefinitionLabel?.label

export const getCustomAttributes = attributes => {
    if (attributes.customAttributeDefinitions) {
        return attributes.customAttributeDefinitions.reduce((acc, definition) => {
            if (definition.attributes.label !== "" || definition.attributes.customFieldDefinitionLabel.label !== "") {
                acc.push({
                    customAttributeDefinitionId: definition.id,
                    label: definition.attributes.label || definition.attributes.customFieldDefinitionLabel.label,
                    value: "",
                    rowId: ""
                })
            }
            return acc
        }, [])
    }
    return null
}

export const isCustomDefinitionAttributePropsEqual = ({
    prevPropsAttributeIndex,
    prevPropsCustomAttributes,
    nextPropsAttributeIndex,
    nextPropsCustomAttributes
}: {
    prevPropsAttributeIndex: number
    prevPropsCustomAttributes: Array<ProductCustomDefinitionAttributes>
    nextPropsAttributeIndex: number
    nextPropsCustomAttributes: Array<ProductCustomDefinitionAttributes>
}): boolean => {
    let isPropsEqual = true
    // Compare attribute addion/deletion
    if (!isEqual(prevPropsCustomAttributes, nextPropsCustomAttributes)) {
        isPropsEqual = false
    }
    if (
        !isEqual(
            getProductCustomDefinitionAttributesMetadata(prevPropsCustomAttributes, prevPropsAttributeIndex),
            getProductCustomDefinitionAttributesMetadata(nextPropsCustomAttributes, nextPropsAttributeIndex)
        )
    ) {
        isPropsEqual = false
    }
    // Compare first layer label
    const prevFirstLayerLabel = getProductCustomDefinitionAttributesFirstLayerLabel(prevPropsCustomAttributes, prevPropsAttributeIndex)
    const nextFirstLayerLabel = getProductCustomDefinitionAttributesFirstLayerLabel(nextPropsCustomAttributes, nextPropsAttributeIndex)
    if (prevFirstLayerLabel !== nextFirstLayerLabel) {
        isPropsEqual = false
    }
    // Compare is required attribute
    const prevIsRequired = getProductCustomDefinitionAttributesRequired(prevPropsCustomAttributes, prevPropsAttributeIndex)
    const nextIsRequired = getProductCustomDefinitionAttributesRequired(nextPropsCustomAttributes, nextPropsAttributeIndex)
    if (prevIsRequired !== nextIsRequired) {
        isPropsEqual = false
    }
    // Compare if allow more than one choice
    const prevIsAllowMoreThanOneChoice = getProductCustomDefinitionAttributesAllowMoreThanOneChoice(
        prevPropsCustomAttributes,
        prevPropsAttributeIndex
    )
    const nextIsAllowMoreThanOneChoice = getProductCustomDefinitionAttributesAllowMoreThanOneChoice(
        nextPropsCustomAttributes,
        nextPropsAttributeIndex
    )
    if (prevIsAllowMoreThanOneChoice !== nextIsAllowMoreThanOneChoice) {
        isPropsEqual = false
    }
    return isPropsEqual
}

export const matchCustomDefnToValue = (itemCustomAttributeValues, itemCustomAttributeDefinitions, rowId) => {
    const itemsList: ItemCustomAttributeValue[] = []
    if (
        itemCustomAttributeValues &&
        itemCustomAttributeValues.length > 0 &&
        itemCustomAttributeDefinitions &&
        itemCustomAttributeDefinitions.length > 0
    ) {
        itemCustomAttributeDefinitions.forEach(def => {
            const match = itemCustomAttributeValues.filter(val => val.attributes.customAttributeDefinitionId === def.id)
            if (match.length > 0) {
                if (def.attributes !== undefined && def.attributes.attributeType === "text") {
                    if (def.attributes.label !== "") {
                        itemsList.push({
                            customAttributeDefinitionId: def.id,
                            label: def.attributes.label,
                            textValue: match[0].attributes.value,
                            rowId: rowId
                        })
                    } else if (def.attributes.customFieldDefinitionLabel.label !== "") {
                        itemsList.push({
                            customAttributeDefinitionId: def.id,
                            label: def.attributes.customFieldDefinitionLabel.label,
                            textValue: match[0].attributes.textValue,
                            rowId: rowId
                        })
                    }
                } else if (def.attributes.attributeType === "select" && def.attributes.customFieldDefinitionLabel.metadata) {
                    itemsList.push({
                        customAttributeDefinitionId: def.id,
                        label: def.attributes.customFieldDefinitionLabel.label,
                        selectValue: match[0].attributes.selectValue,
                        rowId: rowId
                    })
                } else {
                    itemsList.push({
                        customAttributeDefinitionId: def.id,
                        label: def.attributes.label,
                        textValue: match[0].attributes.textValue,
                        rowId: rowId
                    })
                }
            }
        })
        return itemsList
    }
    return null
}

export const getHoverAttributes = (label: string, def: ProductCustomDefinitionDto) => {
    const selectValues = def.attributes.customFieldDefinitionLabel?.metadata?.map(m => m.label).join(", ") || ""
    const isRequired = def.attributes.required ? "*" : ""
    const attributes = selectValues ? `(${selectValues})` : itemCustomAttributesMessages["quote.item_custom_attributes_display.not_configured_msg"]
    return `${label}${isRequired}: ${attributes}`
}

export const getSelectedAttributes = (selectedValues: string[] | undefined, customFieldDefinitionLabel: ProductCustomDefinitionAttributesLabel) => {
    let valueLabel = ""

    if (selectedValues && customFieldDefinitionLabel.metadata) {
        selectedValues.forEach(value => {
            const matchingMetadata = customFieldDefinitionLabel.metadata.find(m => m.id === value)

            if (matchingMetadata) {
                if (valueLabel !== "") {
                    valueLabel += ", " + matchingMetadata.label
                } else {
                    valueLabel = matchingMetadata.label
                }
            }
        })
    }

    return valueLabel
}

export const customAttributesAreValid = (itemCustomAttributes, customAttributeDefinitions) => {
    if (!itemCustomAttributes || itemCustomAttributes.length < 1 || !customAttributeDefinitions) {
        return true
    }

    for (const itemIndex in itemCustomAttributes) {
        const definition = customAttributeDefinitions.find(def => def.id == itemCustomAttributes[itemIndex].customAttributeDefinitionId)
        if (definition?.attributes.attributeType == "text" && definition.attributes.required && !itemCustomAttributes[itemIndex].textValue) {
            return false
        }
        if (
            definition?.attributes.attributeType == "select" &&
            definition.attributes.required &&
            (!itemCustomAttributes[itemIndex].selectValue || itemCustomAttributes[itemIndex].selectValue?.length < 1)
        ) {
            return false
        }
    }
    return true
}

export const isCustomAttributeRequired = (customAttributeDefinitions, customAttributeValue) => {
    if(customAttributeDefinitions && customAttributeValue) {
        const customDefinition = customAttributeDefinitions.find(attr => attr.id == customAttributeValue.customAttributeDefinitionId)
        return customDefinition !== undefined ? customDefinition.attributes.required : false
    }
    return false
}


// WEBPACK FOOTER //
// ./src/utils/customAttributes/utils.ts